/**
 * Sound and haptics, installed once for the whole app.
 *
 * Same shape as `ripple.ts`, and for the same reason: ONE delegated listener
 * rather than a handler per button, so a button written next month gets the
 * app's feel without anyone remembering to wire it. The difference is which way
 * the default points — a ripple is opt-out because every press deserves to be
 * acknowledged, a noise is opt-IN because a sound that fires on everything
 * tells you nothing. `cues.ts` holds that rule.
 *
 * Two ways to fire a cue, and the split is about WHEN the app knows:
 *
 *   - `data-cue="ate"` on a button, for a press that IS the decision. No
 *     import, no handler, no prop threading — and it fires the instant the
 *     button is activated, which is well before the Firestore write returns.
 *   - `cue("add")` from anywhere, for an outcome that arrives later and might
 *     not arrive at all. A write that failed must not have chimed on the way in.
 *
 * Audio is loaded through `use-sound`, which lazy-imports Howler on mount —
 * ~10KB that no first paint waits for, and nothing can play before a gesture
 * anyway. One sprite, one Howl, one request; see `scripts/sounds.mjs`.
 */

import { useCallback, useEffect, useState } from "react";
import useSound from "use-sound";
import {
  buzz,
  CUE_HAPTICS,
  CUE_SPRITE,
  type CueName,
  cueForPress,
  readCuePref,
  writeCuePref,
} from "./cues";

/**
 * The live channel, or null before `useCues` has mounted and after it goes.
 *
 * A module-level slot rather than a context, because the delegated listener
 * below is not a component and never will be — and because a cue fired from a
 * `catch` block should not have to be inside the tree to be heard.
 */
let channel: ((name: CueName) => void) | null = null;

/** Fire a cue. Silent, never throwing, if audio is off or not yet ready. */
export function cue(name: CueName): void {
  channel?.(name);
}

export interface CueApi {
  enabled: boolean;
  setEnabled: (on: boolean) => void;
}

/**
 * Mount the audio and publish the channel. Called exactly once, from `App`.
 *
 * Returns the switch so Settings can render it. The preference gates the
 * haptics as well as the sound: they are one feature to the person using it,
 * and a "sound off" that still buzzed would read as a bug.
 */
export function useCues(): CueApi {
  const [enabled, setEnabled] = useState(readCuePref);

  const [play] = useSound("/sounds/cues.mp3", {
    sprite: CUE_SPRITE,
    // The per-cue balance is already baked into the sprite (a press tick is
    // deliberately a third of a chime). This is the master, set low: these
    // land under whatever else the phone is doing, not over it. Checked on a
    // real handset — the level is right there, which is the only place that
    // counts. A desktop needs its volume further up to hear the tick at all.
    volume: 0.45,
    // Rapid taps replace rather than pile up. Three answers in three seconds
    // is the normal check-in, and overlapping chimes turn it into a chord.
    interrupt: true,
    soundEnabled: enabled,
  });

  useEffect(() => {
    channel = (name) => {
      if (!enabled) return;
      play({ id: name });
      buzz(CUE_HAPTICS[name]);
    };
    return () => {
      channel = null;
    };
  }, [play, enabled]);

  const set = useCallback(
    (on: boolean) => {
      setEnabled(on);
      writeCuePref(on);
      // Turning it on demonstrates itself. `forceSoundEnabled` because the Howl
      // is still holding the old `false` this render — and because a switch
      // whose effect you cannot hear until the next screen is a switch people
      // flip twice to check.
      if (on) {
        play({ id: "add", forceSoundEnabled: true });
        buzz(CUE_HAPTICS.add);
      }
    },
    [play],
  );

  return { enabled, setEnabled: set };
}

/**
 * The delegated listener. Installed from `main.tsx`, beside the ripple.
 *
 * On `click`, not `pointerdown` — which is where this deliberately parts
 * company with the ripple next door. A ripple that starts on a touch that
 * turns into a scroll is a smear nobody registers; a NOISE on that same touch
 * is a phone chirping while you scroll a list, and there is no touch-slop
 * handling here that would tell the two apart. Click also means a cue fires
 * for Enter and Space, which pointer events never see, so the keyboard gets
 * the same confirmation the thumb does.
 */
export function installCues(doc: Document = document): () => void {
  const onClick = (e: MouseEvent) => {
    if (!(e.target instanceof Element)) return;
    const name = cueForPress(e.target);
    if (name) cue(name);
  };

  // Capture, so a handler calling stopPropagation cannot cost the feedback.
  doc.addEventListener("click", onClick, true);
  return () => doc.removeEventListener("click", onClick, true);
}
